import { useState } from 'react';
import {
  CheckCircle, XCircle, Eye, RefreshCw, FileText, Newspaper,
  MapPin, AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/Table';
import {
  useMatchingStats,
  usePendingMatches,
  useUpdateMatchStatus,
  useRunMatching
} from '@/hooks/useMatching';

const tipoBadge: Record<string, { label: string; variant: 'info' | 'warning' | 'success' | 'danger' | 'default' }> = {
  licitacao: { label: 'Licitação', variant: 'info' },
  noticia: { label: 'Notícia', variant: 'warning' }
};



function formatCurrency(value: number | undefined) {
  if (!value) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0
  }).format(value);
}

function ScoreBar({ score }: { score: number }) {
  let color = 'bg-ds-danger';
  if (score >= 75) color = 'bg-ds-success';
  else if (score >= 50) color = 'bg-ds-warning';
  else if (score >= 25) color = 'bg-ds-info';

  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-2 rounded-full bg-ds-surface2 overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs font-medium text-ds-text-primary">{score}</span>
    </div>
  );
}

function RevisaoMatches() {
  const [selectedMatch, setSelectedMatch] = useState<number | null>(null);
  const [rejeicaoMotivo, setRejeicaoMotivo] = useState('');
  const [showRejeicaoModal, setShowRejeicaoModal] = useState(false);

  const { data: statsData, isLoading: statsLoading } = useMatchingStats();
  const { data: matchesData, isLoading: matchesLoading } = usePendingMatches();
  const updateStatus = useUpdateMatchStatus();
  const runMatching = useRunMatching();

  const stats = statsData;
  const matches = matchesData?.matches || [];

  const handleAprovar = async (matchId: number) => {
    await updateStatus.mutateAsync({ matchId, status: 'aprovado' });
  };

  const handleRejeitar = async () => {
    if (!selectedMatch) return;
    await updateStatus.mutateAsync({
      matchId: selectedMatch,
      status: 'rejeitado',
      motivo: rejeicaoMotivo || undefined
    });
    setShowRejeicaoModal(false);
    setRejeicaoMotivo('');
    setSelectedMatch(null);
  };

  const openRejeicao = (matchId: number) => {
    setSelectedMatch(matchId);
    setShowRejeicaoModal(true);
  };

  const statCards = [
    {
      title: 'Pendentes',
      value: stats?.pendentes || 0,
      icon: AlertTriangle,
      color: 'text-ds-warning',
      bg: 'bg-ds-warning/10'
    },
    {
      title: 'Aprovados',
      value: stats?.aprovados || 0,
      icon: CheckCircle,
      color: 'text-ds-success',
      bg: 'bg-ds-success/10'
    },
    {
      title: 'Rejeitados',
      value: stats?.rejeitados || 0,
      icon: XCircle,
      color: 'text-ds-danger',
      bg: 'bg-ds-danger/10'
    },
    {
      title: 'Enviados',
      value: stats?.enviados || 0,
      icon: Eye,
      color: 'text-ds-info',
      bg: 'bg-ds-info/10'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ds-text-primary">Revisão de Matches</h1>
          <p className="text-ds-text-secondary mt-1">
            Avalie e aprove as correlações automáticas entre conteúdo e grupos
          </p>
        </div>
        <Button
          onClick={() => runMatching.mutateAsync({})}
          isLoading={runMatching.isPending}
          variant="secondary"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Executar Matching
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Card key={stat.title}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-ds-text-secondary">{stat.title}</p>
                  <p className="text-2xl font-bold text-ds-text-primary mt-1">
                    {statsLoading ? '-' : stat.value}
                  </p>
                </div>
                <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center`}>
                  <stat.icon className={`h-5 w-5 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Matches Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Matches Pendentes</CardTitle>
              <CardDescription>
                Conteúdos correlacionados automaticamente aguardando revisão
              </CardDescription>
            </div>
            <Badge variant="warning" size="sm">
              {matches.length} pendente{matches.length !== 1 ? 's' : ''}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {matchesLoading ? (
            <p className="text-center text-ds-text-secondary py-8">Carregando matches...</p>
          ) : matches.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 text-ds-success mx-auto mb-3" />
              <p className="text-ds-text-secondary">Nenhum match pendente de revisão</p>
              <p className="text-sm text-ds-text-secondary mt-1">
                Execute o matching para gerar novas correlações
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Conteúdo</TableHead>
                  <TableHead>Grupo</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {matches.map((match) => (
                  <TableRow key={match.id}>
                    <TableCell>
                      <Badge
                        variant={tipoBadge[match.tipo_conteudo]?.variant || 'default'}
                        size="sm"
                      >
                        {match.tipo_conteudo === 'licitacao' ? (
                          <FileText className="h-3 w-3 mr-1" />
                        ) : (
                          <Newspaper className="h-3 w-3 mr-1" />
                        )}
                        {tipoBadge[match.tipo_conteudo]?.label || match.tipo_conteudo}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-xs">
                        <p className="text-sm font-medium text-ds-text-primary truncate">
                          {match.conteudo?.titulo || 'Sem título'}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          {match.conteudo?.orgao && (
                            <span className="text-xs text-ds-text-secondary">
                              {match.conteudo.orgao}
                            </span>
                          )}
                          {match.conteudo?.uf && (
                            <Badge variant="info" size="sm">
                              <MapPin className="h-3 w-3 mr-0.5" />
                              {match.conteudo.uf}
                            </Badge>
                          )}
                          {match.conteudo?.valor_estimado !== undefined && (
                            <span className="text-xs text-ds-success">
                              {formatCurrency(match.conteudo.valor_estimado)}
                            </span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-ds-text-primary">{match.grupo_nome}</span>
                    </TableCell>
                    <TableCell>
                      <ScoreBar score={match.score} />
                    </TableCell>
                    <TableCell>
                      <p className="text-xs text-ds-text-secondary max-w-[200px] truncate">
                        {match.motivo || '-'}
                      </p>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleAprovar(match.id)}
                          isLoading={updateStatus.isPending}
                          className="text-ds-success hover:text-ds-success hover:bg-ds-success/10"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Aprovar
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => openRejeicao(match.id)}
                          className="text-ds-danger hover:text-ds-danger hover:bg-ds-danger/10"
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Rejeitar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Rejection Modal */}
      {showRejeicaoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-ds-surface border border-ds-border rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg bg-ds-danger/10 text-ds-danger">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-ds-text-primary">Rejeitar Match</h3>
                <p className="text-ds-text-secondary mt-1 text-sm leading-relaxed">
                  Informe o motivo da rejeição (opcional):
                </p>
                <textarea
                  value={rejeicaoMotivo}
                  onChange={(e) => setRejeicaoMotivo(e.target.value)}
                  placeholder="Ex: Conteúdo não relevante para o grupo..."
                  className="mt-3 w-full rounded-lg border border-ds-border bg-ds-surface2 px-3 py-2 text-sm text-ds-text-primary placeholder:text-ds-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-ds-success/50 focus:border-ds-success/50 resize-none"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <Button variant="ghost" onClick={() => setShowRejeicaoModal(false)}>
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={handleRejeitar}
                isLoading={updateStatus.isPending}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Rejeitar Match
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { RevisaoMatches };
